import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge, Modal, Button, Typography, notification } from 'antd';
import { MessageOutlined, PhoneOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import { beepMessage, beepRing, buzz } from '../utils/beep';

const { Text, Title } = Typography;

// Header chat icon + global live-chat alert. Runs on EVERY page: polls for new
// messages, beeps, and shows a full-page "join live chat" popup when an admin
// (or anyone) starts a chat with you.
export default function LiveChatNotifier() {
  const [count, setCount] = useState(0);
  const [invite, setInvite] = useState(null);
  const lastIdRef = useRef(0);
  const firstLoadRef = useRef(true);
  const navigate = useNavigate();
  const location = useLocation();
  const onChatPage = location.pathname === '/live-chat';

  // Browsers only allow sound after a user gesture — unlock on the first click.
  useEffect(() => {
    const unlock = () => { try { beepMessage(); } catch (e) { /* ignore */ } };
    const once = () => { unlock(); window.removeEventListener('pointerdown', once); };
    window.addEventListener('pointerdown', once, { once: true });
    return () => window.removeEventListener('pointerdown', once);
  }, []);

  const poll = useCallback(async () => {
    try {
      const res = await api.get('/chat/unread');
      const c = res.data?.count || 0;
      const latest = res.data?.latest || null;
      setCount(c);

      if (latest && Number(latest.id) > lastIdRef.current) {
        const isNew = !firstLoadRef.current || lastIdRef.current === 0;
        lastIdRef.current = Number(latest.id);
        if (isNew && !onChatPage) {
          if (Number(latest.is_invite) === 1) {
            setInvite(latest);
            beepRing();
            buzz([200, 100, 200, 100, 200]);
          } else {
            beepMessage();
            buzz();
            notification.open({
              key: 'live-chat-msg',
              message: `${latest.from_name} — new message`,
              description: String(latest.message).slice(0, 160),
              icon: <MessageOutlined style={{ color: '#4f46e5' }} />,
              duration: 12,
              placement: 'topRight',
              onClick: () => { notification.destroy('live-chat-msg'); navigate('/live-chat'); },
              btn: (
                <Button type="primary" size="small"
                  onClick={() => { notification.destroy('live-chat-msg'); navigate('/live-chat'); }}>
                  Open Chat
                </Button>
              )
            });
          }
        }
      }
      if (c === 0) setInvite(null);
      firstLoadRef.current = false;
    } catch (err) { /* ignore */ }
  }, [navigate, onChatPage]);

  useEffect(() => {
    poll();
    const t = setInterval(() => { if (!document.hidden) poll(); }, 10000);
    return () => clearInterval(t);
  }, [poll]);

  const joinChat = () => { setInvite(null); navigate('/live-chat'); };

  return (
    <>
      <Badge count={count} size="small" offset={[-2, 2]}>
        <MessageOutlined
          title="Live Chat"
          style={{ fontSize: 20, cursor: 'pointer', color: count > 0 ? '#4f46e5' : undefined }}
          onClick={() => navigate('/live-chat')}
        />
      </Badge>

      <Modal
        open={!!invite}
        onCancel={() => setInvite(null)}
        footer={null}
        centered
        width={480}
        maskStyle={{ background: 'rgba(79,70,229,0.35)', backdropFilter: 'blur(2px)' }}
      >
        <div style={{ textAlign: 'center', padding: '12px 4px' }}>
          <PhoneOutlined style={{ fontSize: 44, color: '#4f46e5' }} />
          <Title level={4} style={{ marginTop: 12, marginBottom: 4 }}>
            {invite?.from_name} live chat karna chahte hain
          </Title>
          <Text type="secondary">{invite?.message}</Text>
          <div style={{ marginTop: 22 }}>
            <Button type="primary" size="large" icon={<MessageOutlined />} onClick={joinChat}>
              Join Live Chat
            </Button>
            <Button size="large" style={{ marginLeft: 10 }} onClick={() => setInvite(null)}>
              Baad me
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
